import { Inject, Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { INativeStorageService, NATIVE_STORAGE_SERVICE } from '@boxx/core';
import { IUserProps, UserModel } from '../models/user.model';
import {
  ILoginForm,
  ILoginRepository,
  ISaveTokenDataResponse,
  ISocialLoginForm,
  LOGIN_REPOSITORY,
} from '../repositories/ILogin.repository';
import { ILoginService } from './ILogin.service';
import { IBrandigApiProps } from '@boxx/dynamic-branding/lib/services/dynamic-branding-service';

const helper = new JwtHelperService();
export const USER_TOKEN_KEY = 'user_auth_token';
export const USER_LANGUAGE = 'selectedLanguage';
export const TUTORIAL_SEEN_KEY = 'tutorial';
export const IS_NEW_USER = 'user_new';
export const USER_COUNTRY = 'country';

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function jwtOptionsFactory(localStge: INativeStorageService) {
  return {
    tokenGetter: () => localStge.getItem(USER_TOKEN_KEY),
  };
}

@Injectable()
export class LoginService implements ILoginService {
  private user: UserModel;
  constructor(
    @Inject(NATIVE_STORAGE_SERVICE) private localStge: INativeStorageService,
    @Inject(LOGIN_REPOSITORY) private repository: ILoginRepository
  ) { }

  login(credentials: ILoginForm | ISocialLoginForm): Observable<UserModel> {
    return this.repository.login(credentials).pipe(
      map((response) => {
        if (response.status !== 'success' || !response.data.token) {
          throw new Error('No token was received');
        }

        this.localStge.setItem(USER_TOKEN_KEY, response.data.token);

        const decodedToken = helper.decodeToken(response.data.token);
        const payload: IUserProps = {
          ...decodedToken,
          branding: response.data.brand,
        };

        this.user = new UserModel(payload);

        this.localStge.setItem('username', this.user.name);

        if (response.data.brand.site_language_codes !== undefined) {
          this.localStge.setItem('sitelanguages', response.data.brand.site_language_codes);
        }
        else{
          console.log('no es multi languages')
        }

        return this.user;
      }),
      catchError((e) => {
        throw e;
      })
    );
  }

  socialLogin(credentials: ISocialLoginForm): Observable<UserModel> {
    return this.repository.socialLogin(credentials).pipe(
      map((response) => {
        if (response.status !== 'success' || !response.data.token) {
          throw new Error('No token was received');
        }

        this.localStge.setItem(USER_TOKEN_KEY, response.data.token);

        const decodedToken = helper.decodeToken(response.data.token);

        this.user = UserModel.fromTokenData(decodedToken);

        this.localStge.setItem('username', this.user.name);

        return this.user;
      }),
      catchError((e) => {
        console.error(
          'CATCH: AuthenticationService.socialLogin() -> repository.socialLogin()',
          e
        );
        throw e;
      })
    );
  }

  logout(): Observable<boolean> {
    return new Observable((subcriber) => {
      this.localStge
        .clearStorage()
        .then(async (_) => {
          await this.localStge.removeItem('BRANDING');
          await this.localStge.setItem(TUTORIAL_SEEN_KEY, 'OK');
          await this.localStge.removeItem('tutorial');
          await this.localStge.removeItem('user_auth_token');
          this.user = undefined;
          //await this.localStge.clearStorage();  

          //this.localStge.setItem('BRANDING', JSON.stringify(BOXX_BRAND));
          subcriber.next(true);
        })
        .catch((e) => {
          console.error(
            'AuthenticationService.logout()->this.localStge.clearStorage()',
            e
          );
          subcriber.error(e);
        });
    });
  }

  deleteAccount(): Promise<any> {
    return new Promise((resolve) => {
      this.repository.deleteAccount().subscribe(async () => {
        await this.localStge.removeItem('BRANDING');
        await this.localStge.setItem(TUTORIAL_SEEN_KEY, 'OK');
        await this.localStge.removeItem('tutorial');
        await this.localStge.clearStorage();
        resolve("Resolved");
      })
    });
  }

  saveFCMToken(
    deviceId: string,
    deviceToken: string
  ): Observable<ISaveTokenDataResponse> {
    return this.repository.saveFCMToken(deviceId, deviceToken).pipe(
      map((response) => response.data),
      catchError((e) => {
        console.error(
          'CATCH: AuthenticationService.saveFCMToken() -> repository.saveFCMToken()',
          e
        );
        throw e;
      })
    );
  }

  logSessionStart() {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return this.repository.logSessionStart({ action_type: 'login' }).pipe(
      map((response) => response),
      catchError((error) => {
        throw error;
      })
    );
  }

  sendPasswordRecoveryEmail(email: string): Observable<{ action: string }> {
    return this.repository
      .sendPasswordRecoveryEmail(email)
      .pipe(map((resp) => resp.data.attributes));
  }

  async getSession() {
    const token = await this.localStge.getItem(USER_TOKEN_KEY);
    const decodedToken = helper.decodeToken(token);
    const user = UserModel.fromTokenData(decodedToken);
    return user;
  }

  getUser() {
    return this.user;
  }

  getUserName() {
    return this.localStge.getItem('username');
  }

  decodeUserToken(token: string) {
    return helper.decodeToken(token);
  }

  getThemeData(brand: string): Observable<IBrandigApiProps> {
    return this.repository
      .getThemeData(brand)
      .pipe(map((response) => {
        if (response.data && response.data.id) {
          response.data.attributes.brand_id = response.data.id;
        }
        return response.data.attributes
      }));
  }

  sendRecoveryCode(email: string): Observable<IBrandigApiProps> {
    return this.repository
      .sendRecoveryCode(email)
      .pipe(map((response) => response.data.attributes))
  }

  sendDeleteCode(email: string): Observable<IBrandigApiProps> {
    return this.repository
      .sendDeleteCode(email)
      .pipe(map((response) => response.data.attributes))
  }

  deleteUser(code: string): Observable<IBrandigApiProps> {
    return this.repository
      .deleteUser(code)
      .pipe(map((response) => response.data.attributes))
  }

  validateResetCode(data: any): Observable<IBrandigApiProps> {
    return this.repository
      .validateResetCode(data)
      .pipe(map((response) => response.data.attributes))
  }

  resetPassword(data: any): Observable<IBrandigApiProps> {
    return this.repository
      .resetPassword(data)
      .pipe(map((response) => response.data.attributes))
  }
}

export const BOXX_BRAND: Partial<IBrandigApiProps> = {
  name: 'boxx',
  primary_color: '#0c2340',
  primary_color_contrast: '#ffffff',
  primary_text_color: '#0c2340',
  //secondary
  secondary_color: '#00b5e2',
  secondary_color_contrast: '#ffffff',
  secondary_text_color: '#1e81b0',
  //tertiary
  third_color: '#dd550c',
  third_color_contrast: '#ffffff',

  logo: '/assets/icon/Icon_Header_Brand.svg',
  privacy_url: 'https://myboxx.app/aviso-de-privacidad/',
  support_email: 'ladg@virket.com',
  terms_url: 'https://myboxx.app/terms-user-boxx/',
  tradename: 'Boxx',

  button_color: '#05b300',
  tab_color: '#9c9c9a',
  panel_text_color: '#ffffff',
  contrast_text_color: '#ec0000',
  default_letter_text_color: '#ffffff',
  default_letter_bg_color: '#722545',
  tab_active_color: '#ec0000',
};